import {
  ArcxToken,
  ArcxTokenFactory,
  ArcxTokenV2,
  ArcxTokenV2Factory,
} from '@arcxgame/contracts/dist/src/typings'
import { BigNumber } from '@ethersproject/bignumber'
import { Web3Provider } from '@ethersproject/providers'
import { useWeb3React } from '@web3-react/core'
import { useEffect, useState } from 'react'
import { loadContracts } from 'src/utils'

export const useTokenMigration = () => {
  const isPlaynet = false
  const { active, account, chainId, library } = useWeb3React<Web3Provider>()

  const [hasApproved, setHasApproved] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasClaimableTokens, setHasClaimableTokens] = useState(false)
  const [error, setError] = useState<string>(null)

  const [oldBalance, setOldBalance] = useState<BigNumber>(BigNumber.from(0))
  const [newBalance, setNewBalance] = useState<BigNumber>(BigNumber.from(0))

  const [oldArcxContract, setOldArcxContract] = useState<ArcxToken>(null)
  const [newArcxContract, setNewArcxContract] = useState<ArcxTokenV2>(null)

  // Check if user has claimable tokens
  useEffect(() => {
    const init = async () => {
      if (!active) {
        setHasClaimableTokens(false)
        return
      }

      // Get address of the new arcx token
      let deployment = loadContracts(
        chainId,
        {
          name: 'ArcxToken',
          source: 'ArcxTokenV2',
          version: 2,
        },
        isPlaynet,
      )
      if (deployment.length !== 1) {
        setError('ARCX Token V2 could not be found')
        return
      }

      const newArcxAddress = deployment[0].address
      if (!newArcxAddress) {
        setError('The address of ARCX V2 could not be found')
        return
      }

      // Get old arcx token
      deployment = loadContracts(
        chainId,
        {
          name: 'ArcxToken',
          source: 'ArcxToken',
          version: 1,
        },
        isPlaynet,
      )
      if (deployment.length !== 1) {
        setError('ARCX Token (old) could be found')
        return
      }

      const oldArcxAddress = deployment[0].address
      if (!oldArcxAddress) {
        setError('The address of ARCX (old) could not be found')
        return
      }

      const oldArcx = ArcxTokenFactory.connect(
        oldArcxAddress,
        library.getSigner(account),
      )
      const newArcx = ArcxTokenV2Factory.connect(
        newArcxAddress,
        library.getSigner(account),
      )

      // Get user's old and new arcx balance
      console.log(`old arcx addr: ${oldArcx.address}`)
      const oldBalance = await oldArcx.balanceOf(account)
      const newBalance = await newArcx.balanceOf(account)

      if (oldBalance.gt(0)) {
        setHasClaimableTokens(true)

        // Check if user approved tokens
        const allowance = await oldArcx.allowance(account, newArcx.address)
        if (allowance.eq(oldBalance)) {
          setHasApproved(true)
        }
      }

      // Check allowance
      if (oldBalance.gt(0)) {
        const allowance = await oldArcx.allowance(account, newArcxAddress)
        console.log(`allowance: ${allowance.toString()}`)

        if (allowance.gte(oldBalance)) {
          setHasApproved(true)
        }
      }

      // Save balances to state
      setOldBalance(oldBalance)
      setNewBalance(newBalance)

      // Save contracts to state
      setOldArcxContract(oldArcx)
      setNewArcxContract(newArcx)
    }

    init()
  }, [active, chainId, account, library, isPlaynet])

  const onApproveClicked = async () => {
    if (!checkPreconditions()) {
      return
    }

    const balance = await oldArcxContract.balanceOf(account)

    if (balance.isZero()) {
      console.log('Old token balance is 0')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const tx = await oldArcxContract.approve(newArcxContract.address, balance)
      await tx.wait()

      setHasApproved(true)
    } catch (err) {
      console.error(err)
      setError(
        `There was an error when approving tokens. Please check the logs`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onClaimClicked = async () => {
    if (!checkPreconditions()) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const tx = await newArcxContract.claim()
      await tx.wait()

      setHasClaimableTokens(false)

      // Update balance
      setNewBalance(await newArcxContract.balanceOf(account))
    } catch (err) {
      console.error(err)
      setError(
        `There was an error when migrating tokens. Please check the logs`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const checkPreconditions = (): boolean => {
    if (!account) {
      setError('Account is not set')
      return false
    }

    if (!newArcxContract?.address) {
      setError('Could not get the address of the new ARCX token')
      return false
    }

    return true
  }

  return {
    hasApproved,
    hasClaimableTokens,
    onApproveClicked,
    onClaimClicked,
    error,
    isLoading,
    oldBalance,
    newBalance,
  }
}
